'use client';

import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import type { CompanyAccountStatus, CompanyAccountUser } from '@open-design/contracts';
import { COMPANY_BRAND } from '../brand';
import { useI18n } from '../i18n';
import { applyAppearanceToDocument } from '../state/appearance';
import { ProductBrand } from '../components/ProductBrand';
import { Icon } from '../components/Icon';
import { LanguageMenu } from '../components/LanguageMenu';
import { companyAccountAction, companyAccountRequest } from './api';
import { setCompanyProjectAccount } from './project-cache';
import styles from './CompanyAccountGate.module.css';

const AccountContext = createContext<CompanyAccountUser | null>(null);
export const useCompanyAccount = () => useContext(AccountContext);

export function CompanyAccountGate({ children }: { children: ReactNode }) {
  const { locale } = useI18n();
  const zh = locale.startsWith('zh');
  const [session, setSession] = useState<CompanyAccountStatus | null>(null);
  const [error, setError] = useState('');
  const [loggingOut, setLoggingOut] = useState(false);
  const [settingPassword, setSettingPassword] = useState(false);
  const sessionVersion = useRef(0);
  const adoptSession = useCallback((next: CompanyAccountStatus) => {
    setCompanyProjectAccount(next.enabled && next.authenticated ? next.user?.id ?? null : null);
    setSession(next);
  }, []);
  // The workspace mounts only after sign-in, so its appearance effect cannot
  // initialize the account screen (especially when inline scripts are blocked).
  useLayoutEffect(() => { applyAppearanceToDocument({}); }, []);
  const refresh = useCallback(async () => {
    const version = sessionVersion.current;
    try {
      const next = await companyAccountRequest('session');
      if (version !== sessionVersion.current) return;
      adoptSession(next); setError('');
    } catch (error) {
      if (version === sessionVersion.current) setError(error instanceof Error ? error.message : '账号服务暂时不可用');
    }
  }, [adoptSession]);
  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => { if (document.visibilityState === 'visible') void refresh(); }, 30_000);
    window.addEventListener('focus', refresh);
    return () => { window.clearInterval(timer); window.removeEventListener('focus', refresh); };
  }, [refresh]);

  async function logout() {
    if (loggingOut) return;
    sessionVersion.current++;
    setLoggingOut(true);
    try {
      await companyAccountAction('logout', {});
      adoptSession({ enabled: true, authenticated: false, user: null, smsConfigured: session?.smsConfigured });
      setSettingPassword(false);
      setError('');
    } catch (error) { setError(error instanceof Error ? error.message : '退出失败，请重试'); }
    finally { setLoggingOut(false); }
  }

  if (session && !session.enabled) return <>{children}</>;
  if (session?.authenticated && session.user) return (
    <AccountContext.Provider key={session.user.id} value={session.user}>
      <div className={styles.workspace}>
        <header className={styles.accountBar}>
          <span className={styles.workspaceName}>{COMPANY_BRAND.productName}</span>
          <div className={styles.accountActions}>
            {error ? <span role="alert" className={styles.connectionError}>{error}</span> : null}
            <span className={styles.accountName}><Icon name="users" size={15} />{session.user.nickname}</span>
            <button type="button" onClick={() => setSettingPassword(true)}><Icon name="lock" size={15} />{zh ? '设置密码' : 'Set password'}</button>
            <button type="button" onClick={logout} disabled={loggingOut}>
              <Icon name="log-out" size={15} />{loggingOut ? (zh ? '正在退出…' : 'Signing out…') : (zh ? '退出登录' : 'Sign out')}
            </button>
          </div>
        </header>
        <div className={styles.workspaceBody}>{children}</div>
        {settingPassword ? <AccountForm modal ready smsConfigured={session.smsConfigured !== false} initialView="set-password" initialPhone={session.user.phone} onCancel={() => setSettingPassword(false)} onAuthenticated={next => { sessionVersion.current++; adoptSession(next); setSettingPassword(false); }} /> : null}
      </div>
    </AccountContext.Provider>
  );
  return <AccountPage smsConfigured={session?.smsConfigured !== false} ready={Boolean(session)} error={error} onRetry={refresh} onAuthenticated={next => { sessionVersion.current++; adoptSession(next); }} />;
}

type AccountView = 'sms' | 'password' | 'set-password';
type AccountFormProps = {
  ready: boolean;
  smsConfigured: boolean;
  error?: string;
  onRetry?: () => Promise<void>;
  onAuthenticated: (status: CompanyAccountStatus) => void;
  initialView?: AccountView;
  initialPhone?: string;
  modal?: boolean;
  onCancel?: () => void;
};

function AccountForm({ ready, smsConfigured, error: connectionError, onRetry, onAuthenticated, initialView = 'sms', initialPhone = '', modal = false, onCancel }: AccountFormProps) {
  const { locale } = useI18n();
  const zh = locale.startsWith('zh');
  const id = useId();
  const dialog = useRef<HTMLDialogElement>(null);
  const [view, setView] = useState<AccountView>(initialView);
  const [phone, setPhone] = useState(initialPhone);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const setting = view === 'set-password';
  const needsSms = view !== 'password';
  useEffect(() => { if (modal) dialog.current?.showModal(); }, [modal]);
  useEffect(() => {
    if (!cooldown) return;
    const timer = window.setTimeout(() => setCooldown(value => Math.max(0, value - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);
  function switchView(next: AccountView) {
    if (busy || sending) return;
    setView(next); setError(''); setNotice(''); setCode(''); setPassword(''); setConfirmation(''); setShowPassword(false);
  }
  async function sendCode() {
    if (sending || cooldown || busy) return;
    if (!/^1[3-9]\d{9}$/.test(phone.trim())) { setError(zh ? '请输入正确的手机号' : 'Enter a valid Chinese mobile number'); return; }
    setSending(true); setError(''); setNotice('');
    try {
      const result = await companyAccountAction('send-code', { phone: phone.trim(), ...(setting ? { purpose: 'password' } : {}) });
      setNotice(result.message); setCooldown(60);
    } catch (error) { setError(error instanceof Error ? error.message : '验证码发送失败'); }
    finally { setSending(false); }
  }
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy || sending || !ready || needsSms && !smsConfigured) return;
    setError('');
    if (setting && password !== confirmation) { setError(zh ? '两次输入的密码不一致' : 'Passwords do not match'); return; }
    setBusy(true);
    try {
      const action = setting ? 'set-password' : view === 'password' ? 'login-password' : 'login-phone';
      const body = { phone: phone.trim(), ...(needsSms ? { code: code.trim() } : {}), ...(view !== 'sms' ? { password } : {}) };
      const result = await companyAccountRequest(action, body);
      if (!result.authenticated || !result.user) throw new Error(zh ? '登录未完成，请重试' : 'Sign-in did not complete. Please try again.');
      setCode(''); setPassword(''); setConfirmation(''); onAuthenticated(result);
    } catch (error) { setError(error instanceof Error ? error.message : '请求失败，请稍后重试'); }
    finally { setBusy(false); }
  }
  const contents = <>
    <h1 id={`${id}-title`}>{setting ? (zh ? '设置／重置密码' : 'Set or reset password') : (zh ? '手机号登录' : 'Sign in with your phone')}</h1>
    <p className={styles.subtitle}>{setting ? (zh ? '通过短信验证手机号，设置后即可使用密码登录。' : 'Verify your phone by SMS to set your sign-in password.') : (zh ? '选择短信验证码或密码，继续你的创作。' : 'Continue creating with an SMS code or password.')}</p>
    {!setting ? <div className={styles.methods} aria-label={zh ? '登录方式' : 'Sign-in method'}>
      <button type="button" aria-pressed={view === 'sms'} disabled={busy || sending} onClick={() => switchView('sms')}>{zh ? '短信登录' : 'SMS code'}</button>
      <button type="button" aria-pressed={view === 'password'} disabled={busy || sending} onClick={() => switchView('password')}>{zh ? '密码登录' : 'Password'}</button>
    </div> : null}
    {(error || connectionError) ? <div className={styles.error} role="alert">{error || connectionError}{!ready && onRetry ? <button type="button" onClick={onRetry}>{zh ? '重新连接' : 'Retry'}</button> : null}</div> : null}
    {ready && needsSms && !smsConfigured ? <div className={styles.error} role="alert">{zh ? '短信服务尚未配置，请联系管理员完成配置。' : 'SMS service is not configured. Contact the administrator.'}</div> : null}
    {notice ? <p className={styles.notice} role="status">{notice}</p> : null}
    <form onSubmit={submit} aria-busy={busy}>
      <fieldset disabled={busy || !ready || needsSms && !smsConfigured} className={styles.fields}>
        <label htmlFor={`${id}-phone`}>{zh ? '手机号' : 'Mobile number'}<input id={`${id}-phone`} name="phone" type="tel" readOnly={modal && Boolean(initialPhone)} autoComplete={view === 'password' ? 'username' : 'tel'} inputMode="tel" placeholder={zh ? '请输入手机号' : 'Chinese mobile number'} pattern="1[3-9][0-9]{9}" maxLength={11} value={phone} onChange={e => { setPhone(e.target.value); setCode(''); }} required /></label>
        {needsSms ? <label htmlFor={`${id}-code`}>{zh ? '短信验证码' : 'SMS verification code'}<span className={styles.codeRow}><input id={`${id}-code`} name="code" inputMode="numeric" autoComplete="one-time-code" placeholder={zh ? '请输入6位验证码' : '6-digit verification code'} pattern="[0-9]{6}" maxLength={6} value={code} onChange={e => setCode(e.target.value)} required /><button type="button" onClick={sendCode} disabled={sending || cooldown > 0}>{sending ? (zh ? '发送中…' : 'Sending…') : cooldown ? `${cooldown}s` : (zh ? '获取验证码' : 'Send code')}</button></span></label> : null}
        {view !== 'sms' ? <label htmlFor={`${id}-password`}>{setting ? (zh ? '新密码' : 'New password') : (zh ? '密码' : 'Password')}<span className={styles.passwordRow}><input id={`${id}-password`} name="password" type={showPassword ? 'text' : 'password'} autoComplete={setting ? 'new-password' : 'current-password'} placeholder={zh ? '请输入8–128位密码' : '8–128 characters'} minLength={8} maxLength={128} value={password} onChange={e => setPassword(e.target.value)} required /><button type="button" onClick={() => setShowPassword(value => !value)} aria-label={showPassword ? (zh ? '隐藏密码' : 'Hide password') : (zh ? '显示密码' : 'Show password')} aria-pressed={showPassword}><Icon name={showPassword ? 'eye-off' : 'eye'} size={18} /></button></span></label> : null}
        {setting ? <label htmlFor={`${id}-confirmation`}>{zh ? '确认密码' : 'Confirm password'}<input id={`${id}-confirmation`} type={showPassword ? 'text' : 'password'} autoComplete="new-password" minLength={8} maxLength={128} value={confirmation} onChange={e => setConfirmation(e.target.value)} required /></label> : null}
        <button type="submit" disabled={sending} className={styles.submit}>{busy ? <><Icon name="spinner" size={17} />{zh ? '正在处理…' : 'Please wait…'}</> : !ready ? (zh ? '正在连接…' : 'Connecting…') : setting ? (zh ? '保存密码并登录' : 'Save password and sign in') : (zh ? '登录并开始使用' : 'Sign in and start')}<Icon name="arrow-right" size={17} /></button>
      </fieldset>
    </form>
    <div className={styles.formActions}>
      {!setting ? <button type="button" disabled={busy || sending} onClick={() => switchView('set-password')}>{zh ? '设置／忘记密码？' : 'Set or forgot password?'}</button> : !modal ? <button type="button" disabled={busy || sending} onClick={() => switchView('sms')}>{zh ? '返回登录' : 'Back to sign in'}</button> : null}
      {modal ? <button type="button" disabled={busy || sending} onClick={onCancel}>{zh ? '取消' : 'Cancel'}</button> : null}
    </div>
    <p className={styles.accountHint}><Icon name="lock" size={13} />{setting ? (zh ? '密码更新后，其他登录会话将失效。' : 'Updating your password signs out other sessions.') : view === 'sms' ? (zh ? '首次验证自动创建本工具账号，无需邮箱。' : 'First sign-in creates a tool account. No email needed.') : (zh ? '尚未设置密码？可通过短信验证设置。' : 'No password yet? Set one after SMS verification.')}</p>
  </>;
  return modal ? <dialog ref={dialog} className={styles.passwordDialog} aria-labelledby={`${id}-title`} onCancel={event => { event.preventDefault(); if (!busy && !sending) onCancel?.(); }}>{contents}</dialog> : contents;
}

function AccountPage(props: AccountFormProps) {
  const { locale } = useI18n();
  const zh = locale.startsWith('zh');
  return (
    <main className={styles.page}>
      <header className={styles.header}><ProductBrand /><LanguageMenu placement="down" align="end" /></header>
      <div className={styles.layout}>
        <section className={styles.panel} aria-label={zh ? '工作台账号' : 'Workspace account'}>
          <div className={styles.card}><AccountForm {...props} /></div>
        </section>
        <aside className={styles.story} aria-label={zh ? '阿宝创作工作台' : 'Abao creative workspace'}>
          <span className={styles.eyebrow}>ABAO CREATIVE WORKSPACE</span>
          <h2>{zh ? <>每一个想法，<br />都有新的可能。</> : <>Every idea.<br />A new possibility.</>}</h2>
          <p>{zh ? '把灵感带进来，让阿宝陪你一起完成。' : 'Bring your inspiration. Create something with Abao.'}</p>
          <div className={styles.illustration}><img src={COMPANY_BRAND.avatar} alt="" width={560} height={560} /><span className={styles.note}>{zh ? '从灵感，到作品' : 'From inspiration to creation'}</span></div>
          <div className={styles.features}><span>{zh ? '原型设计' : 'Prototypes'}</span><span>{zh ? '演示文稿' : 'Presentations'}</span><span>{zh ? '多媒体创作' : 'Media creation'}</span></div>
        </aside>
      </div>
      <footer className={styles.footer}>© {new Date().getFullYear()} {COMPANY_BRAND.name} · {zh ? '让灵感成为作品' : 'Bring ideas to life'}</footer>
    </main>
  );
}
